/**
 * State for an Element Edit session.
 *
 * Editing a saved element reuses the one and only editor document: the open
 * template is captured to plain data and parked here, the element is installed
 * in its place as an ordinary document (one artboard per saved size), and the
 * template is rebuilt from the park on exit. Every editing tool then works
 * untouched — from their point of view nothing unusual is happening.
 *
 * This store holds only what the session needs to save and to restore. The
 * orchestration lives in `services/editor/element/elementEditSession`.
 *
 * Deliberately dependency-light: `historyStore` resolves its default scope
 * through `active` here, so this store must not import it back.
 */
import { acceptHMRUpdate, defineStore } from 'pinia';
import type { SizeConfiguration } from '@/types/creativeElement';
import type { AdId } from '@/types/layers/layer';
import type {
  ElementEditState,
  ElementSizeBinding,
  ParkedTemplate
} from '@/types/stores/elementEdit';

export const useElementEditStore = defineStore('elementEdit', {
  state: (): ElementEditState => ({
    active: false,
    componentId: null,
    name: '',
    tag: 0,
    rootLayerId: null,
    sizeBindings: [],
    parked: null,
    entering: false,
    saving: false
  }),

  getters: {
    /** Original ad id for a session artboard, or null for a size added in-session. */
    originalAdIdOf:
      state =>
      (sessionAdId: AdId): string | null =>
        state.sizeBindings.find(b => b.sessionAdId === sessionAdId)?.config.adId ?? null,

    /** The size config bound to a session artboard. */
    configForAd:
      state =>
      (sessionAdId: AdId): SizeConfiguration | null =>
        state.sizeBindings.find(b => b.sessionAdId === sessionAdId)?.config ??
        null
  },

  actions: {
    /** Wipes every session field. Called on exit, after the template is back. */
    reset() {
      this.active = false;
      this.componentId = null;
      this.name = '';
      this.tag = 0;
      this.rootLayerId = null;
      this.sizeBindings = [];
      this.parked = null;
      this.entering = false;
      this.saving = false;
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useElementEditStore, import.meta.hot));
}
